import { Observable, OperatorFunction } from 'rxjs';

import {
  DashboardFilter,
  DashboardTileTabValues,
  DashboardTileType,
  InstallationType,
} from '@store';
import {
  AggregatedStatus,
  DeviceRoles,
  InvitationAccessLevel,
  SubscriptionPlan,
  ViError,
} from '@vi-iot/client';
import { DhwOperatingMode } from '../../../../app/operational-data-common/translations';
import { GenericDataResponse } from '../analytics/analytics.model';

export interface DashboardApiModel {
  getDashboard(): Observable<GenericDataResponse<DashboardColumn[]>>;

  recreateSnapshot(): Observable<GenericDataResponse<DashboardColumn[]>>;

  getFilters(): Observable<GenericDataResponse<DashboardFilterResponse[]>>;

  setFilters(filters: DashboardFilterPayload): Observable<GenericDataResponse<DashboardColumn[]>>;

  addColumn(column: DashboardAddColumnPayload): Observable<GenericDataResponse<DashboardColumn>>;

  deleteColumn(id: number, version: string): Observable<GenericDataResponse<null>>;

  editColumn(
    id: number,
    body: DashboardEditColumnPayload
  ): Observable<GenericDataResponse<DashboardColumnDataWithoutInstallationDetails>>;

  fetchInstallationForColumn(
    data: GetColumnInstallationsPayload,
    filters: DashboardFilterPayload,
    sortOrder: SortOrder | null // value needed for demo mode
  ): Observable<GenericDataResponse<DashboardInstallationData[]>>;

  moveKanbanInstallation(
    body: [
      DashboardColumnDataWithoutInstallationDetails,
      DashboardColumnDataWithoutInstallationDetails
    ]
  ): Observable<
    GenericDataResponse<
      [DashboardColumnDataWithoutInstallationDetails, DashboardColumnDataWithoutInstallationDetails]
    >
  >;

  getFilteredInstallationsV1(
    offset: number,
    limit: number,
    body: DashboardFilterPayload,
    withQuickView: boolean
  ): Observable<GenericDataResponse<DashboardInstallationData[]>>;

  getFilteredInstallationsV2(
    limit: number,
    body: DashboardFilterPayload,
    cursor?: string
  ): Observable<DashboardInstallationListResponse>;

  fetchInvitationsRecursively(
    filter: DashboardFilterPayload,
    limit?: number
  ): OperatorFunction<DashboardInstallationListResponse, DashboardInstallationData[]>;

  getInstallationListFilterButtonData(): Observable<GenericDataResponse<ButtonFilterData[]>>;

  getSumsData(body: ButtonFilterDataPayload[]): Observable<GenericDataResponse<ButtonFilterData[]>>;
}

export interface DashboardColumn {
  id: number;
  leftNeighbor: number | null;
  name: string;
  selfDefined: boolean;
  sortOrder: SortOrder;
  version: string;
  installations: DashboardInstallationData[];
  installationIds: number[];
}

export interface DashboardInstallationData {
  address: DashboardInstallationAddress;
  aggregatedStatus: AggregatedStatus;
  amountOfAnomalies: {
    [AnomalySeverity.Warning]?: number;
    [AnomalySeverity.Information]?: number;
    [AnomalySeverity.WorkingFine]?: number;
    [AnomalySeverity.Optimization]?: number;
    [AnomalySeverity.Critical]?: number;
    [AnomalySeverity.RootCause]?: number;
  };
  commercialOwner: DashboardCommercialCompany;
  consumers: DashboardConsumer[];
  description: string | null;
  device: DashboardMainDevice;
  devices: DashboardDevice[];
  id: number;
  installationType: InstallationType | null;
  isActiveSmartNote: boolean;
  isCascade: boolean;
  isMultiGateway: boolean;
  isSystemLocked: boolean;
  isStandalone: boolean;
  editedAt: Date;
  connectedAt: Date;
  subscriptionExpirationDate?: Date;
  isTrialSubscription?: boolean;
  owner: {
    firstName: string;
    lastName: string;
    email: string;
    mobileNumber: string;
    phoneNumber: string;
  };
  partners: DashboardCommercialCompany[];
  plans: SubscriptionPlan[];
  smartNotes: DashboardSmartNote[];
}

export interface DashboardInstallationAddress {
  street: string;
  houseNumber: string;
  zip: string;
  city: string;
  geolocation: {
    latitude: number;
    longitude: number;
    timeZone: string;
  };
}

export interface DashboardMainDevice {
  vin: string;
  marketingName: string;
  materialImage: string;
  plans: SubscriptionPlan[];
  dhwOperatingMode: null;
  heatingOperatingModes: [];
  roles: DeviceRoles[];
  brandLogo?: string;
}

export interface DashboardDevice {
  vin: string;
  marketingName: string;
  heatingOperatingModes: DashboardHeatingOperatingMode[];
  dhwOperatingMode: DhwOperatingMode;
  heatingServiceTimeBased: null | {
    nextService: string;
  };
  heatingServiceBurnerBased: null | {
    serviceIntervalBurnerHours: number;
    activeBurnerHoursSinceLastService: number;
  };
  smartNotes: DashboardSmartNote[];
  anomalies: DashboardAnomaly[];
  plans: SubscriptionPlan[];
  materialImage: string;
  roles: DeviceRoles[];
  brandLogo?: string;
}

export interface DashboardHeatingOperatingMode {
  circuitId: number;
  mode: DashboardHeatingOperatingModeValue;
}

export interface DashboardSmartNote {
  id: number;
  status: string;
  type: string;
  content: {
    gatewaySerial: string;
    deviceId: string;
    modelId: string;
    errorCode: string;
    appearedAt: string;
    equipmentType: string;
    errorEventType: string;
    installationId: number;
  };
  createdAt: string;
  editedAt: string;
  targetGroups: InvitationAccessLevel[];
}

export interface DashboardAnomaly {
  id: string;
  diagnosisName: string;
  text: string;
  severity: AnomalySeverity;
}

export interface FaultMessageSmartnotes {
  smartNotes: DashboardSmartNote[];
  needTooltip: boolean;
}

export enum DashboardHeatingOperatingModeValue {
  standby = 'standby',
  dhw = 'dhw',
  dhwAndHeatingCooling = 'dhwAndHeatingCooling',
  forcedReduced = 'forcedReduced',
  forcedNormal = 'forcedNormal',
  normalStandby = 'normalStandby',
  cooling = 'cooling',
  unknown = 'unknown',
  heating = 'heating',
  dhwAndHeating = 'dhwAndHeating',
}

export interface DashboardCommercialCompany {
  companyId: string;
  companyName: string;
}

export interface DashboardConsumer {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  address: DashboardInstallationAddress;
}

export enum SortOrder {
  asc = 'asc',
  desc = 'desc',
}

export interface DashboardAddColumnPayload {
  id: number;
  leftNeighbor: number;
  name: string;
  sortOrder: SortOrder;
  installations: number[];
}

export interface DashboardEditColumnPayload {
  leftNeighbor: number;
  version: string;
  name: string;
  installations?: number[]; // needed only for self defined columns
  sortOrder: SortOrder;
}

export interface DashboardFilterResponse {
  filterName: DashboardFilterName;
  type: DashboardFilterType;
  values: DashboardFilterValues[] | null;
}

export enum DashboardFilterType {
  TextSearch = 'text-search',
  Complex = 'complex',
  Toggle = 'toggle',
}

export type DashboardFilterValues =
  | DashboardTypeActiveAnomalies
  | DashboardTypeDhwOperatingMode
  | DashboardTypeHeatingOperatingMode
  | DashboardTypeDhwPaidPackage
  | DashboardTypeInstallationType
  | DashboardTypeAggregatedStatus
  | DashboardActiveAnomaliesStatus
  | DashboardTypeProductName
  | DashboardAnomaliesNamesEnum;

export type DashboardTypeActiveAnomalies =
  | 'critical'
  | 'warning'
  | 'optimization'
  | 'working fine'
  | 'information';
export type DashboardTypeDhwOperatingMode =
  | 'balanced'
  | 'off'
  | 'comfort'
  | 'eco'
  | 'efficient'
  | 'efficientWithMinComfort';
export type DashboardTypeHeatingOperatingMode =
  | 'heating'
  | 'standby'
  | 'testMode'
  | 'auto'
  | 'cooling'
  | 'heatingCooling'
  | 'dhw'
  | 'dhwAndHeatingCooling';
export type DashboardTypeDhwPaidPackage = 'Pro' | 'Plus' | 'Free';
export type DashboardTypeInstallationType = 'Residential' | 'Commercial';
export type DashboardTypeAggregatedStatus =
  | 'Error'
  | 'Offline'
  | 'Maintenance'
  | 'WorksProperly'
  | 'RemoteDiagnosticSession'
  | 'NbIotConnected';
export type DashboardActiveAnomaliesStatus =
  | 'critical'
  | 'warning'
  | 'optimization'
  | 'root_cause'
  | 'information';

export enum AnomalySeverity {
  RootCause = 'root_cause',
  Information = 'information',
  Optimization = 'optimization',
  Warning = 'warning',
  Critical = 'critical',
  WorkingFine = 'working fine',
}

export type DashboardTypeProductName =
  | 'Vitoair FS'
  | 'Vitocal 222-SI'
  | 'Vitocal 250-A'
  | 'Vitocal 252-A'
  | 'Vitocharge VX3'
  | 'Vitodens 100-W'
  | 'Vitodens 200-W'
  | 'Vitodens 222-F'
  | 'Vitodens 333-F'
  | 'Vitodens 343-F'
  | 'Vitovalor 300-P'
  | 'Vitovalor PT2';

export interface DashboardInstallationsTile {
  storeState: string;
  filter?: DashboardInstallationsFilterPayload;
  kanbanBoardFilter?: DashboardFilter[];
  tabs?: {
    value: DashboardTileTabValues;
    storeState: string;
    filter: DashboardInstallationsFilterPayload;
    showCounter: boolean;
    kanbanBoardFilter?: DashboardFilter[];
  }[];
  withQuickView?: boolean;
}

export interface DashboardInstallationsTileTranslations {
  tileEmptyData: string;
  tabs?: Partial<
    Record<
      DashboardTileTabValues,
      {
        tabName: string;
        tabEmptyData: string;
      }
    >
  >;
}

export interface DashboardInstallationsFilterPayload {
  filterName: string;
  type: DashboardInstallationsFilterType;
  values: DashboardInstallationsFilterValuesType;
  modifier?: string;
}

export enum DashboardInstallationsFilterNames {
  AggregatedStatus = 'aggregated-status',
  RecentlyUpdated = 'recently-updated',
  RecentlyConnected = 'recently-connected',
  HemsNotActivated = 'hems-not-activated',
  ActiveAnomalies = 'active-anomalies',
  SrcNotActivated = 'src-not-activated',
  PaidPackages = 'paid-package',
  AnomaliesNames = 'anomalies-names',
}

export enum DashboardInstallationsFilterType {
  TimeBased = 'time-based',
  Toggle = 'toggle',
  Complex = 'complex',
}

export type DashboardInstallationsFilterValuesType =
  | DashboardInstallationsFilterValues[]
  | DashboardInstallationsFilterValues;

export type DashboardInstallationsFilterValues =
  | DashboardInstallationsStatusEnum
  | DashboardInstallationTimeBasedEnum
  | DashboardActiveAnomaliesEnum
  | DashboardPaidPackagesEnum
  | DashboardAnomaliesNamesEnum
  | true
  | false;

export enum DashboardInstallationTimeBasedEnum {
  Today = 'Today',
  Yesterday = 'Yesterday',
  Last7Days = 'Last7Days',
  Last30Days = 'Last30Days',
  LastYear = 'LastYear',
}

export enum DashboardInstallationsStatusEnum {
  Error = 'Error',
  Maintenance = 'Maintenance',
  Offline = 'Offline',
  WorksProperly = 'WorksProperly',
  RemoteDiagnosticSession = 'RemoteDiagnosticSession',
  Locked = 'Locked',
}

export enum DashboardActiveAnomaliesEnum {
  Critical = 'critical',
  Warning = 'warning',
  Optimization = 'optimization',
  WorkingFine = 'working fine',
  Information = 'information',
}

export enum DashboardPaidPackagesEnum {
  Pro = 'Pro',
  Plus = 'Plus',
}

export enum DashboardAnomaliesNamesEnum {
  OnlineAvaibilityState = 'online_availability_state',
  LeakageGradientAnomaly = 'leakage_gradient_anomaly',
  LeakageLossAnomaly = 'leakage_loss_anomaly',
  LeakageThresholdAnomalyLower = 'leakage_threshold_anomaly_lower',
  RemainingUsefulLifeOfFuelCell = 'remaining_useful_life_of_fuel_cell',
  OutputGradientAnomaly = 'output_gradient_anomaly',
  OutputThresholdAnomaly = 'output_threshold_anomaly',
  DhwDurationAnomaly = 'dhw_duration_anomaly',
  DhwEfficiencyAnomaly = 'dhw_efficiency_anomaly',
  DhwStartsAnomaly = 'dhw_starts_anomaly',
  BurnerAnomaly = 'burner_anomaly',
  GasFanAnomaly = 'gas_fan_anomaly',
  InternalPumpAnomaly = 'internal_pump_anomaly',
  ThreeWayValveAnomaly = '3way_valve_anomaly',
  TooLowRuntimeCheck = 'too_low_runtime_check',
  OutsideTemperatureSensor = 'outside_temperature_sensor',
  FlueTemperatureHeating = 'flue_temperature_heating',
  FlueTemperatureDhw = 'flue_temperature_dhw',
  FuelCellRuntimeNoRootCauseFound = 'fuel_cell_runtime_no_root_cause_found',
  FuelCellRuntimeSensorsCheck = 'fuel_cell_runtime_sensors_check',
  FuelCellRuntimeCirculationCheck = 'fuel_cell_runtime_circulation_check',
  FuelCellRuntimeUsageCheck = 'fuel_cell_runtime_usage_check',
  FuelCellRuntimeSetPointCheck = 'fuel_cell_runtime_set_point_check',
  BurnerClockingCheck = 'burner_clocking_check',
  WaterRefill = 'water_refill',
  OutletTemperaturePrediction = 'outlet_temperature_prediction',
  SolarThermalCheck = 'solar_thermal_check',
  SolarCollectorSensor = 'solar_collector_sensor',
  SolarDhwSensor = 'solar_dhw_sensor',
  VolumetricFlowDhwCheck = 'volumetric_flow_dhw_check',
  HeatingRodDhwDuration = 'heating_rod_dhw_duration',
  HeatingRodHeatingBivalence = 'heating_rod_heating_bivalence',
  Algo03NightSwitchOns = 'algo03_night_switch_ons',
  Algo04NightCirculation = 'algo04_night_circulation',
  Algo05aDecreasingDhwPumpEvents = 'algo05a_decreasing_dhw_pump_events',
  Algo05bQ25DhwTempAtStop = 'algo05b_q25_dhw_temp_at_stop',
  Algo05cQ50DhwTempAtStop = 'algo05c_q50_dhw_temp_at_stop',
  Algo06Stagnation = 'algo06_stagnation',
  Algo07HighDifferenceSolarPumpEvents = 'algo07_high_difference_solar_pump_events',
  Algo08DecreasingCollectorStops = 'algo08_decreasing_collector_stops',
  Algo09TemperatureArea = 'algo09_temperature_area',
  CompressorClockingCheck = 'compressor_clocking_check',
  IgnitionTimesCheck = 'ignition_times_check',
  CompressorAnomaly = 'compressor_anomaly',
  ThreeWayValveHeatpumpAnomaly = '3way_valve_heatpump_anomaly',
  HeatingHeatpumpCheck = 'heating_heatpump_check',
  DhwHeatpumpCheck = 'dhw_heatpump_check',
  CoolingHeatpumpCheck = 'cooling_heatpump_check',
  HeatingBurnerCheck = 'heating_burner_check',
  DhwBurnerCheck = 'dhw_burner_check',
  RefrigerantPressure = 'refrigerant_pressure',
  RefrigerantLeakageShortTerm = 'refrigerant_leakage_short_term',
  RefrigerantLeakageLongTerm = 'refrigerant_leakage_long_term',
  MembraneExpansionVesselCheck = 'membrane_expansion_vessel_check',
  DhwChargingsCheck = 'dhw_chargings_check',
  HydrSeparatorSensorCheck = 'hydr_separator_sensor_check',
  HeatingHydrSepToMainCheck = 'heating_hydr_sep_to_main_check',
  BurnerClockingLongTermCheck = 'burner_clocking_long_term_check',
  CompressorClockingLongTermCheck = 'compressor_clocking_long_term_check',
  RefrigerantLeakageShortTermDhw = 'refrigerant_leakage_short_term_dhw',
  RefrigerantLeakageShortTermCooling = 'refrigerant_leakage_short_term_cooling',
  OutletTempSensor = 'outlet_temp_sensor',
  FuelCellRuntimeErrorCheck = 'fuel_cell_runtime_error_check',
  ConnectionSwitchOffCheck = 'connection_switch_off_check',
  MainHeatExchangerDhwConfigCheck = 'main_heat_exhanger_dhw_config_check',
  VolFlowGradientAnomalyDhw = 'vol_flow_gradient_anomaly_dhw',
  VolFlowLossAnomalyDhw = 'vol_flow_loss_anomaly_dhw',
  GasConsumptionCalculation = 'gas_consumption_calculation',
  AmbientTemperatureCheck = 'ambient_temperature_check',
  ForwardFilledGeneralActivityFeatures = 'forward_filled_general_activity_features',
  VolFlowGradientAnomalyDefrost = 'vol_flow_gradient_anomaly_defrost',
  VolFlowLossAnomalyDefrost = 'vol_flow_loss_anomaly_defrost',
  HeatingRodHeatingEmergencyModeCheck = 'heating_rod_heating_emergency_mode_check',
  HeatingRodHeatingScreedDryingCheck = 'heating_rod_heating_screed_drying_check',
  HeatingRodHeatingPowerOffCheck = 'heating_rod_heating_power_off_check',
  HeatingRodHeatingNoiseReductionCheck = 'heating_rod_heating_noise_reduction_check',
  HeatingRodHeatingDefrostCheck = 'heating_rod_heating_defrost_check',
  DefrostPreparationEfficiency = 'defrost_preparation_efficiency',
  DefrostEfficiency = 'defrost_efficiency',
  EvaporationEfficiency = 'evaporation_efficiency',
  HeatingRodHeatingModeOnErrorCheck = 'heating_rod_heating_mode_on_error_check',
  HeatingRodHeatingBlockingHpErrorCheck = 'heating_rod_heating_blocking_hp_error_check',
  InletToOutsideTempSensorCheck = 'inlet_to_outside_temp_sensor_check',
  HeatingRodHeatingHighModulationErrorCheck = 'heating_rod_heating_high_modulation_error_check',
  HeatingRodHeatingPauseErrorCheck = 'heating_rod_heating_pause_error_check',
  RefrigerantPressureE3 = 'refrigerant_pressure_e3',
  SpfYear = 'spf_year',
  SpfMonth = 'spf_month',
  HeatCurveOptimization = 'heat_curve_optimization',
  HeatingInSummer = 'heating_in_summer',
  CompressorModulationActiveMinCompressorClocking = 'compressor_modulation_active_min_compressor_clocking',
  CompressorModulationActiveMinHeatingRod = 'compressor_modulation_active_min_heating_rod',
  CompressorModulationActiveMinCompressorClockingLongTerm = 'compressor_modulation_active_min_compressor_clocking_longterm',
  EfficiencyGasBoiler = 'efficiency_gas_boiler',
  EfficiencyVitovalorFuelcell = 'efficiency_vitovalor_fuelcell',
  OverheatCheckHeating = 'overheat_check_heating',
  OverheatCheckDhw = 'overheat_check_dhw',
  OverheatCheckCooling = 'overheat_check_cooling',
  DhwTargetTemperature = 'dhw_target_temperature',
  DhwDuration = 'dhw_duration',
  CapacityCheckBattery0 = 'capacity_check_battery_0',
  CapacityCheckBattery1 = 'capacity_check_battery_1',
  CapacityCheckBattery2 = 'capacity_check_battery_2',
  CapacityCheckBattery3 = 'capacity_check_battery_3',
  CapacityCheckBattery4 = 'capacity_check_battery_4',
  CapacityCheckBattery5 = 'capacity_check_battery_5',
  CapacityCheckElectricityStorage = 'capacity_check_electricity_storage',
  AgingCheckBattery0 = 'aging_check_battery_0',
  AgingCheckBattery1 = 'aging_check_battery_1',
  AgingCheckBattery2 = 'aging_check_battery_2',
  AgingCheckBattery3 = 'aging_check_battery_3',
  AgingCheckBattery4 = 'aging_check_battery_4',
  AgingCheckBattery5 = 'aging_check_battery_5',
  AgingThresholdCalculation = 'aging_threshold_calculation',
  SetbackDurationOptimization = 'setback_duration_optimization',
  ReducedTemperatureOptimization = 'reduced_temperature_optimization',
  NormalTemperatureOptimization = 'normal_temperature_optimization',
  RecommendationForDhwHysteresis = 'recommendation_for_dhw_hysteresis',
  RefrigerantCircuitCheck1 = 'refrigerant_circuit_check1',
  RefrigerantCircuitCheck2 = 'refrigerant_circuit_check2',
  RefrigerantCircuitCheck3 = 'refrigerant_circuit_check3',
  RefrigerantCircuitCheck4 = 'refrigerant_circuit_check4',
  ShgProvidesHeat = 'shg_provides_heat',
  ShgClocking = 'shg_clocking',
  HeatSourceAlternations = 'heat_source_alternations',
  VitoairRuntime = 'vitoair_runtime',
  ConnectionQualityIndicator = 'connection_quality_indicator',
  ConnectionQualityIndicatorCalculation = 'connection_quality_indicator_calculation',
  DeviceInformation = 'device_information',
  LeakageThresholdAnomalyUpper = 'leakage_threshold_anomaly_upper',
  VitoairPipeValidation = 'vitoair_pipe_validation',
  ViairUniformTemperatureReading = 'viair_uniform_temperature_reading',
  VitoventMessageA0 = 'vitovent_message_a0',
  VitoairValiditySensorSup = 'vitoair_validity_sensor_sup',
  VitoairValiditySensorEta = 'vitoair_validity_sensor_eta',
  VitoairValiditySensorEha = 'vitoair_validity_sensor_eha',
  VitoairValiditySensorOda = 'vitoair_validity_sensor_oda',
  VolFlowThresholdAnomalyDhwUpper = 'vol_flow_threshold_anomaly_dhw_upper',
  VolFlowThresholdAnomalyDefrostUpper = 'vol_flow_threshold_anomaly_defrost_upper',
  DhwVictimAnodeThresholdCheck = 'dhw_victim_anode_threshold_check',
  ViairBypassValidationFunctions = 'viair_bypass_validation_functions',
  CapacityVsGuaranteed0 = 'capacity_vs_guaranteed_0',
  CapacityVsGuaranteed1 = 'capacity_vs_guaranteed_1',
  CapacityVsGuaranteed2 = 'capacity_vs_guaranteed_2',
  CapacityVsGuaranteed3 = 'capacity_vs_guaranteed_3',
  CapacityVsGuaranteed4 = 'capacity_vs_guaranteed_4',
  CapacityVsGuaranteed5 = 'capacity_vs_guaranteed_5',
  BatteryNominalCapacityCalculation = 'battery_nominal_capacity_calculation',
  VolFlowThresholdAnomalyDhw = 'vol_flow_threshold_anomaly_dhw',
  VolFlowThresholdAnomalyDefrost = 'vol_flow_threshold_anomaly_defrost',
}

export interface MultipleTileTypesResponse
  extends GenericDataResponse<DashboardInstallationData[]> {
  tileType: DashboardTileType;
  parentTile: DashboardTileType;
  tab: string;
  error: ViError;
}

export interface DashboardFilterPayload {
  [key: string]: string | string[] | boolean;
}

/**
 * Endpoint used for infinite scrolling.
 * It could be used when filters are applied.
 */
export interface GetColumnInstallationsPayload {
  columnId: number;
  offset: number;
  limit: number;
  withQuickView: boolean;
}

export interface DashboardColumnDataWithoutInstallationDetails {
  id: number;
  installations: number[];
  leftNeighbor: number;
  name: string;
  selfDefined: boolean;
  sortOrder: SortOrder;
  version: string;
}

export interface DashboardInstallationsPayload extends PaginationPayload {
  filter?: DashboardInstallationsFilterPayload;
}

export interface PaginationPayload {
  offset: number;
  limit: number;
  withQuickView?: boolean;
}

export interface DashboardInstallationsSumsPayload {
  name: string;
  filters: {
    'active-anomalies'?: DashboardTypeActiveAnomalies;
    'active-smartnote'?: boolean;
    'aggregated-status'?: AggregatedStatus[];
    'dhw-operating-mode'?: DashboardTypeDhwOperatingMode;
    'heating-operating-mode'?: DashboardTypeHeatingOperatingMode;
    'installation-type'?: DashboardTypeInstallationType;
    'hems-activated'?: boolean;
    'hems-not-activated'?: boolean;
    'src-activated'?: boolean;
    'src-not-activated'?: boolean;
    'paid-package'?: DashboardTypeDhwPaidPackage[];
    'product-name'?: string;
    'recently-connected'?: DashboardInstallationTimeBasedEnum;
    'recently-updated'?: DashboardInstallationTimeBasedEnum;
  };
}

export interface DashboardInstallationsSumsResponse {
  name: string;
  value: number;
}

export interface DashboardInstallationsSums {
  tileType: string;
  tab: string;
  counter: number;
}

export interface ButtonFilterData {
  name: ButtonFilterDataName;
  value: number;
}

export enum ButtonFilterDataName {
  All = 'All',
  Error = 'Error',
  Warning = 'Warning',
  Offline = 'Offline',
  Maintenance = 'Maintenance',
  Anomalies = 'Anomalies',
  Working = 'Working',
}

export const ERROR_SNAPSHOT_NOT_FOUND = 'SNAPSHOT_NOT_FOUND';

export interface PaginationPayload {
  offset: number;
  limit: number;
}

export enum DashboardFilterName {
  Fulltext = 'fulltext',
  ActiveAnomalies = 'active-anomalies',
  ActiveSmartnote = 'active-smartnote',
  AnomaliesNames = 'anomalies-names',
  AggregatedStatus = 'aggregated-status',
  DhwOperatingMode = 'dhw-operating-mode',
  HeatingOperatingMode = 'heating-operating-mode',
  InstallationType = 'installation-type',
  HemsActivated = 'hems-activated',
  HemsNotActivated = 'hems-not-activated',
  SrcActivated = 'src-activated',
  SrcNotActivated = 'src-not-activated',
  PaidPackage = 'paid-package',
  ProductName = 'product-name',
  RecentlyConnected = 'recently-connected',
  RecentlyUpdated = 'recently-updated',
  withoutStandaloneInstallations = 'without-standalone-installations',
  withStandaloneInstallations = 'with-standalone-installations',
}

export type ButtonFilterDataPayloadValueArray = {
  [FilterName in DashboardFilterName]?: DashboardFilterValues[] | boolean;
};

export type ButtonFilterDataPayloadValueBoolean = { [FilterName in DashboardFilterName]?: boolean };

export interface ButtonFilterDataPayload {
  name: ButtonFilterDataName;
  filters: ButtonFilterDataPayloadValueArray | ButtonFilterDataPayloadValueBoolean;
}

export interface GenericSumsFilterDataPayload {
  name: string;
  filters: ButtonFilterDataPayloadValueArray | ButtonFilterDataPayloadValueBoolean;
}

// Each new button payload must be adjusted in dashboard api and corresponding effects file

export const errorButtonFilterDataPayload: ButtonFilterDataPayloadValueArray = {
  [DashboardFilterName.AggregatedStatus]: ['Error'],
};

export const offlineButtonFilterDataPayload: ButtonFilterDataPayloadValueArray = {
  [DashboardFilterName.AggregatedStatus]: ['Offline'],
};

export const anomaliesButtonFilterDataPayload: ButtonFilterDataPayloadValueArray = {
  [DashboardFilterName.ActiveAnomalies]: ['critical', 'warning', 'information'],
  [DashboardFilterName.PaidPackage]: ['Pro'],
};
export const maintenanceButtonFilterDataPayload: ButtonFilterDataPayloadValueArray = {
  [DashboardFilterName.AggregatedStatus]: ['Maintenance'],
};

export const worksProperlyButtonFilterDataPayload: ButtonFilterDataPayloadValueArray = {
  [DashboardFilterName.AggregatedStatus]: ['WorksProperly'],
};

export const MAXIMUM_DASHBOARD_LIMIT = 100;

export interface DashboardInstallationListResponse {
  data: DashboardInstallationData[];
  cursor: {
    next: string;
  };
}
